import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import type { ComponentLike } from '@glint/template';
import { PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';
import IconBadge from 'tio-ui/components/icon-badge';
import type { IconBadgeColor } from 'tio-ui/components/icon-badge';
import Collapsible from 'tio-ui/components/collapsible';

interface TaskCardSignature {
  Args: {
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    iconColor?: IconBadgeColor;
    title: string;
    /** Pre-formatted chip text, e.g. "use by 07/01" or "due 07/15". */
    chip?: string;
    defaultOpen?: boolean;
  };
  Blocks: { body: [] };
  Element: HTMLDivElement;
}

/**
 * Generic expandable task card for the Needs Your Attention panel: leading
 * icon badge, title, optional due/use-by chip, and a collapsible body block.
 * Self-manages its open state (seeded by `@defaultOpen`).
 */
export default class TaskCard extends Component<TaskCardSignature> {
  @tracked isOpen = this.args.defaultOpen ?? false;

  /** Stable id linking the toggle button to its collapsible region for a11y. */
  contentId = `task-card-content-${guidFor(this)}`;

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  <template>
    <div class="tio-bg-surface rounded-xl border tio-border-default" ...attributes>
      <button
        type="button"
        class="tio-row-between w-full items-start gap-3 p-4 text-left"
        aria-expanded={{if this.isOpen "true" "false"}}
        aria-controls={{this.contentId}}
        {{on "click" this.toggle}}
      >
        <div class="tio-row min-w-0 items-start gap-3">
          <IconBadge @icon={{@icon}} @color={{if @iconColor @iconColor "purple"}} @size="md" />
          <div class="tio-row min-w-0 flex-wrap items-center gap-2">
            <span class="tio-text-sm-semibold tio-fg-primary">{{@title}}</span>
            {{#if @chip}}
              <span
                class="tio-text-xs-medium tio-fg-info tio-bg-info-soft rounded-full px-2 py-0.5"
              >{{@chip}}</span>
            {{/if}}
          </div>
        </div>
        {{#if this.isOpen}}
          <PhCaretUp @size={{16}} class="tio-fg-secondary shrink-0" />
        {{else}}
          <PhCaretDown @size={{16}} class="tio-fg-secondary shrink-0" />
        {{/if}}
      </button>
      <Collapsible @isOpen={{this.isOpen}} id={{this.contentId}}>
        <div class="px-4 pb-4">
          {{yield to="body"}}
        </div>
      </Collapsible>
    </div>
  </template>
}
